import React, { useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import Papa from "papaparse";
import "./Styles.css";

function QRCodeGenerator() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [input, setInput] = useState("");
  const [customText, setCustomText] = useState("");
  const [qrCodes, setQrCodes] = useState([]);
  const [generatedNumbers, setGeneratedNumbers] = useState([]);
  const [error, setError] = useState("");
  const [numberOption, setNumberOption] = useState("all");
  const [qrCodeSizeInches, setQrCodeSizeInches] = useState(2.5);
  const [logo, setLogo] = useState(null);
  const [transparency, setTransparency] = useState(80);
  const [stepSize, setStepSize] = useState(1);
  const [numberOfDigits, setNumberOfDigits] = useState("");
  const [countToGenerate, setCountToGenerate] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [logoScalePercent, setLogoScalePercent] = useState(100); // 100% = as-is
  const apiUrl = process.env.REACT_APP_API_URL;
  // || "http://localhost:5050/api";

  // console.log("REACT_APP_API_URL ===>", process.env.REACT_APP_API_URL); // temp debug
  // NEW: loading state
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const numbers = new Set();

    let startNum = parseInt(start, 10);
    let endNum = parseInt(end, 10);
    let digits = parseInt(numberOfDigits, 10);
    let count = parseInt(countToGenerate, 10);
    let step = parseInt(stepSize, 10);

    if (numberOption === "text") {
      if (customText.trim() === "") {
        setError("Please enter valid text or URL.");
        return;
      }
      numbers.add(customText);
    } else if (numberOption === "digits") {
      if (isNaN(digits) || digits <= 0) {
        setError("Please enter a valid number of digits.");
        return;
      }
      if (isNaN(count) || count <= 0) {
        setError("Please enter a valid number of counts to generate.");
        return;
      }
      if (isNaN(startNum) || isNaN(endNum) || startNum >= endNum) {
        setError("Please enter valid starting and ending numbers.");
        return;
      }

      while (numbers.size < count) {
        const randomNum =
          Math.floor(Math.random() * (endNum - startNum + 1)) + startNum;
        numbers.add(randomNum.toString());
      }
    } else {
      if (
        numberOption !== "custom" &&
        numberOption !== "import" &&
        (isNaN(startNum) || isNaN(endNum) || startNum > endNum || step <= 0)
      ) {
        setError(
          "Please enter valid numbers with the start less than or equal to the end and step size greater than 0."
        );
        return;
      }

      switch (numberOption) {
        case "all":
          for (let i = startNum; i <= endNum; i += step) {
            numbers.add(i.toString());
          }
          break;
        case "odd":
        case "even": {
          if (numberOption === "even" && startNum % 2 !== 0) startNum += 1;
          if (numberOption === "odd" && startNum % 2 === 0) startNum += 1;
          for (let i = startNum; i <= endNum; ) {
            numbers.add(i.toString());
            i += 2 * step;
          }
          break;
        }
        case "custom":
          const customNumbers = input
            .split(",")
            .map((num) => num.trim())
            .filter((num) => !isNaN(num) && num !== "");
          customNumbers.forEach((num) => numbers.add(num));
          if (numbers.size === 0) {
            setError("Please enter valid numbers.");
            return;
          }
          break;
        case "import":
          // handled elsewhere
          break;
        default:
          break;
      }
    }

    setGeneratedNumbers(Array.from(numbers));
    // show loader while generating
    setIsLoading(true);
    try {
      await generateQRCodes(Array.from(numbers));
    } finally {
      setIsLoading(false);
    }
  };

  const generateQRCodes = async (numbers) => {
    const qrCodeUrls = [];
    for (const num of numbers) {
      try {
        // update to your actual endpoint if needed
        const response = await axios.post(`${apiUrl}/generate-qr`, {
          text: num,
        });

        qrCodeUrls.push(response.data.qrCodeUrl);
      } catch (err) {
        console.error("Error generating QR code for number", num, err);
      }
    }
    setQrCodes(qrCodeUrls);
    setGeneratedNumbers(numbers);
  };

  // Build a square logo overlay with baked-in transparency and scaling.
  // baseSizePx: the QR image size in pixels (canvas width/height)
  async function prepareLogoOverlay(baseSizePx) {
    if (!logo) return null;
    try {
      const imgLogo = await loadImage(logo);

      // ✅ Use a baseline fraction so 100% ≈ 25% of QR (like your old PNG behavior)
      const LOGO_BASELINE = 1; // 100% of QR side at 100%
      let overlaySize = Math.round(
        Math.max(8, baseSizePx * LOGO_BASELINE * (logoScalePercent / 100))
      );

      // Create a square canvas and draw the logo centered
      const canvasL = document.createElement("canvas");
      canvasL.width = overlaySize;
      canvasL.height = overlaySize;
      const ctxL = canvasL.getContext("2d");

      ctxL.clearRect(0, 0, overlaySize, overlaySize);
      ctxL.imageSmoothingEnabled = false; // keep crisp
      ctxL.globalAlpha = Math.min(Math.max(transparency / 100, 0), 1);

      const logoAR = imgLogo.naturalWidth / imgLogo.naturalHeight;
      let drawW, drawH;
      if (logoAR >= 1) {
        drawW = overlaySize;
        drawH = Math.round(overlaySize / logoAR);
      } else {
        drawH = overlaySize;
        drawW = Math.round(overlaySize * logoAR);
      }
      const dx = Math.round((overlaySize - drawW) / 2);
      const dy = Math.round((overlaySize - drawH) / 2);

      ctxL.drawImage(imgLogo, dx, dy, drawW, drawH);
      ctxL.globalAlpha = 1;

      return { dataUrl: canvasL.toDataURL("image/png"), size: overlaySize };
    } catch (e) {
      console.warn("prepareLogoOverlay failed:", e);
      return null;
    }
  }

  const downloadQRCode = async (qrUrl, index) => {
    try {
      // 1) Load the QR image
      const qrImg = new Image();
      qrImg.crossOrigin = "anonymous";
      qrImg.src = qrUrl;
      await new Promise((resolve, reject) => {
        qrImg.onload = resolve;
        qrImg.onerror = reject;
      });

      // Base logical size (CSS pixels)
      const logicalSize = qrImg.naturalWidth || qrImg.width || 400;

      // Render bigger to preserve detail on export
      const exportScale = Math.max(
        1,
        Math.floor((window.devicePixelRatio || 1) * 20)
      );
      const outSize = logicalSize * exportScale;

      // 2) Draw QR onto a high-DPI canvas
      const canvas = document.createElement("canvas");
      canvas.width = outSize;
      canvas.height = outSize;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, outSize, outSize);
      ctx.imageSmoothingEnabled = false; // ✦ critical for sharp modules
      ctx.setTransform(exportScale, 0, 0, exportScale, 0, 0); // scale drawing space

      // draw QR at logical size (transform upsizes pixels without blur)
      ctx.drawImage(qrImg, 0, 0, logicalSize, logicalSize);

      // 3) Prepare & stamp the logo overlay at the *output* pixel size
      const overlay = await prepareLogoOverlay(outSize);
      if (overlay) {
        const overlayImg = await loadImage(overlay.dataUrl);
        const logoX = Math.round((outSize - overlay.size) / 2);
        const logoY = Math.round((outSize - overlay.size) / 2);

        // overlay is already sized in output pixels; draw without additional scaling
        ctx.setTransform(1, 0, 0, 1, 0, 0); // reset transform for exact pixel draw
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(overlayImg, logoX, logoY, overlay.size, overlay.size);
      }

      // 4) Save PNG (robust fallback)
      const download = (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `qr_${index + 1}.png`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      };

      if (canvas.toBlob) {
        canvas.toBlob((blob) => {
          if (blob) return download(blob);
          // Fallback if some browsers return null
          const dataUrl = canvas.toDataURL("image/png");
          const byteString = atob(dataUrl.split(",")[1]);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++)
            ia[i] = byteString.charCodeAt(i);
          download(new Blob([ia], { type: "image/png" }));
        }, "image/png");
      } else {
        const dataUrl = canvas.toDataURL("image/png");
        const byteString = atob(dataUrl.split(",")[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++)
          ia[i] = byteString.charCodeAt(i);
        download(new Blob([ia], { type: "image/png" }));
      }
    } catch (err) {
      console.error("Error generating PNG:", err);
      alert("Could not generate PNG. Check console for details.");
    }
  };

  // helper to build a safe filename for the PDF
  const buildPdfFileName = () => {
    // try to pick sensible start/end values from state or generatedNumbers
    const first = generatedNumbers && generatedNumbers[0];
    const last =
      generatedNumbers && generatedNumbers.length > 0
        ? generatedNumbers[generatedNumbers.length - 1]
        : null;

    let prefix = "";

    if (
      numberOption === "all" ||
      numberOption === "odd" ||
      numberOption === "even"
    ) {
      // prefer explicit start/end states if present, otherwise fallback to generatedNumbers
      const s = start || first;
      const e = end || last;
      if (s && e && s !== e) prefix = `${s}-${e}`;
      else if (s && !e) prefix = `${s}`;
      else if (s === e) prefix = `${s}`;
      else if (first && last && first !== last) prefix = `${first}-${last}`;
      else if (first) prefix = `${first}`;
    } else if (
      numberOption === "custom" ||
      numberOption === "import" ||
      numberOption === "digits"
    ) {
      if (first && last && first !== last) prefix = `${first}-${last}`;
      else if (first) prefix = `${first}`;
    } else if (numberOption === "text") {
      // use customText if provided, shorten and replace spaces
      const t = customText || first || "text";
      prefix = String(t).slice(0, 40); // limit length
    } else {
      // fallback
      if (first && last && first !== last) prefix = `${first}-${last}`;
      else if (first) prefix = `${first}`;
    }

    // add option suffix to be explicit (all/odd/even/custom/text/digits/import)
    const suffix = numberOption || "qr";

    let filename = prefix ? `${prefix}_${suffix}.pdf` : `qr_codes.pdf`;

    // sanitize filename: remove slashes, control chars, replace spaces with underscore
    filename = filename.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9._-]/g, "");

    // final fallback
    if (!filename || filename.length === 0) filename = "qr_codes.pdf";

    return filename;
  };

  // put near top of component (helpers/constants)
  const PX_PER_MM = 3.7795275591; // ~96 DPI pixels per mm

  async function loadImage(urlOrDataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = urlOrDataUrl;
    });
  }

  /**
   * Resize image to targetMM (millimeters) and return a dataURL.
   * - format: "image/png" or "image/jpeg"
   * - quality used only for jpeg
   * - forceSquare will make the output square using width as base
   */
  async function compressImage(
    urlOrDataUrl,
    targetMM,
    { format = "image/png", quality = 1.5, forceSquare = false } = {}
  ) {
    // try load, propagate error to caller to handle if needed
    const img = await loadImage(urlOrDataUrl);

    const targetPx = Math.max(1, Math.round(targetMM * PX_PER_MM));
    let outW = targetPx;
    let outH = Math.round((img.naturalHeight / img.naturalWidth) * targetPx);

    if (forceSquare) outH = outW;

    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");

    // Ensure transparent background for png
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw image scaled
    ctx.drawImage(img, 0, 0, outW, outH);

    if (format === "image/jpeg") {
      return canvas.toDataURL("image/jpeg", quality);
    } else {
      return canvas.toDataURL("image/png");
    }
  }

  // REPLACED exportToPDF (use this instead of your old one)
  const exportToPDF = async () => {
    if (!qrCodes || qrCodes.length === 0) return;
    setIsLoading(true);

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const qrCodeSizeMM = qrCodeSizeInches * 25.4;

      // prepare cached logo as PNG (preserve alpha) and apply user transparency on the canvas
      let cachedLogoDataUrl = null;
      if (logo) {
        try {
          const logoTargetMM = Math.max(6, qrCodeSizeMM * 1.5); // smaller overlay, min 6mm
          // load original logo to compute aspect ratio & apply globalAlpha
          const imgLogo = await loadImage(logo);

          const logoPx = Math.max(1, Math.round(logoTargetMM * PX_PER_MM));
          const outW = logoPx;
          const outH = Math.round(
            (imgLogo.naturalHeight / imgLogo.naturalWidth) * outW
          );

          const canvasL = document.createElement("canvas");
          canvasL.width = outW;
          canvasL.height = outH;
          const ctxL = canvasL.getContext("2d");

          // clear and preserve transparency
          ctxL.clearRect(0, 0, canvasL.width, canvasL.height);

          // apply chosen transparency for overlay (0..1)
          ctxL.globalAlpha = Math.min(Math.max(transparency / 100, 0), 1);

          // draw the logo scaled; because we export PNG, alpha is preserved
          ctxL.drawImage(imgLogo, 0, 0, outW, outH);

          // reset alpha
          ctxL.globalAlpha = 1;

          cachedLogoDataUrl = canvasL.toDataURL("image/png");
        } catch (err) {
          console.warn(
            "Could not prepare cached logo (continuing without logo):",
            err
          );
          cachedLogoDataUrl = null;
        }
      }

      for (let index = 0; index < qrCodes.length; index++) {
        const url = qrCodes[index];

        // compress/rescale QR to exactly the printed size (PNG to keep sharp edges)
        let qrDataUrl = null;
        try {
          qrDataUrl = await compressImage(url, qrCodeSizeMM, {
            format: "image/png",
            forceSquare: true,
          });
        } catch (err) {
          console.warn(
            `Failed to compress QR at index ${index}, falling back to original`,
            err
          );
          qrDataUrl = url; // fallback to original URL/data
        }

        if (index > 0) pdf.addPage();

        const x = (pdf.internal.pageSize.getWidth() - qrCodeSizeMM) / 2;
        const y = (pdf.internal.pageSize.getHeight() - qrCodeSizeMM) / 2;

        // draw QR first
        try {
          pdf.addImage(qrDataUrl, "PNG", x, y, qrCodeSizeMM, qrCodeSizeMM);
        } catch (err) {
          console.warn(
            "pdf.addImage failed for QR (attempting with JPEG):",
            err
          );
          try {
            // attempt JPEG fallback (may lose edge crispness)
            const fallbackQr = await compressImage(qrDataUrl, qrCodeSizeMM, {
              format: "image/jpeg",
              quality: 0.95,
              forceSquare: true,
            });
            pdf.addImage(fallbackQr, "JPEG", x, y, qrCodeSizeMM, qrCodeSizeMM);
          } catch (e) {
            console.error("Fallback QR add failed:", e);
          }
        }

        // then overlay PNG logo (preserves transparency)
        if (cachedLogoDataUrl) {
          const logoSizeMMBase = Math.max(6, qrCodeSizeMM);
          const logoSizeMM = logoSizeMMBase * (logoScalePercent / 100);

          const logoX = x + (qrCodeSizeMM - logoSizeMM) / 2;
          const logoY = y + (qrCodeSizeMM - logoSizeMM) / 2;
          try {
            pdf.addImage(
              cachedLogoDataUrl,
              "PNG",
              logoX,
              logoY,
              logoSizeMM,
              logoSizeMM
            );
          } catch (err) {
            console.warn("pdf.addImage failed for logo:", err);
            // continue without breaking; logo is non-critical
          }
        }
      }

      const filename = buildPdfFileName();
      pdf.save(filename);
      resetFormData();
    } catch (err) {
      console.error("Error exporting PDF:", err);
      alert("Error exporting PDF. See console for details.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetFormData = () => {
    setStart("");
    setEnd("");
    setInput("");
    setCustomText("");
    setQrCodes([]);
    setGeneratedNumbers([]);
    setError("");
    setNumberOption("all");
    setStepSize(1);
    setNumberOfDigits("");
    setCountToGenerate("");
  };

  const exportToCSV = () => {
    const csvData = Papa.unparse(generatedNumbers.map((num) => [num]));
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "generated_numbers.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToCSV2 = () => {
    const csvData = generatedNumbers.join(",");
    const csvContent = [csvData];
    const blob = new Blob([csvContent.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "generated_numbers.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      Papa.parse(file, {
        complete: (results) => {
          const numbers = results.data
            .map((row) => row[0])
            .filter((num) => num);
          if (numbers.length > 0) {
            setGeneratedNumbers(numbers);
            setQrCodes([]);
            // show loader while generating
            setIsLoading(true);
            generateQRCodes(numbers).finally(() => setIsLoading(false));
          }
        },
        header: false,
      });
    }
  };

  const handleRowDoubleClick = () => {
    setShowAll((prev) => !prev);
  };

  return (
    <div className="container">
      <div className="company-logo">
        <img
          src="/logo.png"
          alt="Company logo"
          className="company-logo-image"
          width="160"
          height="auto"
          loading="eager"
        />
      </div>
      <h2>QR Code Generator</h2>

      {/* LOADER OVERLAY */}
      {isLoading && (
        <div className="loader-overlay" aria-live="polite">
          <div className="loader" />
          <div className="loader-text">Processing…</div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <select
            id="numberOption"
            value={numberOption}
            onChange={(e) => {
              const selectedOption = e.target.value;
              setNumberOption(selectedOption);
              setGeneratedNumbers([]);
              setQrCodes([]);
              if (selectedOption === "text") {
                setInput("");
                setCustomText("");
                setStart("");
                setEnd("");
                setNumberOfDigits("");
                setCountToGenerate("");
              } else if (selectedOption === "digits") {
                setNumberOfDigits("");
                setCountToGenerate("");
                setStart("");
                setEnd("");
              } else if (selectedOption === "custom") {
                setInput("");
                setStart("");
                setEnd("");
                setNumberOfDigits("");
                setCountToGenerate("");
              } else {
                setCustomText("");
                setInput("");
                setStart("");
                setEnd("");
                setNumberOfDigits("");
                setCountToGenerate("");
              }
            }}
            disabled={isLoading}
          >
            <option value="all">All Numbers</option>
            <option value="odd">Odd Numbers</option>
            <option value="even">Even Numbers</option>
            <option value="custom">Custom Numbers</option>
            <option value="text">Custom URL/Text</option>
            <option value="digits">Generate Random Numbers</option>
            <option value="import">Import from CSV</option>
          </select>

          {/* ... inputs unchanged but disabled when loading */}
          {(numberOption === "all" ||
            numberOption === "odd" ||
            numberOption === "even") && (
            <div className="range-inputs">
              <input
                type="number"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                placeholder="Enter starting number"
                required
                disabled={isLoading}
              />
              <input
                type="number"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                placeholder="Enter ending number"
                required
                disabled={isLoading}
              />
              <label className="label-input-step">Step Size:</label>
              <input
                type="number"
                value={stepSize}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (value > 0) {
                    setStepSize(value);
                  } else {
                    setStepSize(1);
                  }
                }}
                min="1"
                required
                disabled={isLoading}
              />
            </div>
          )}

          {numberOption === "custom" && (
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter numbers separated by commas"
              required
              disabled={isLoading}
            />
          )}
          {numberOption === "text" && (
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Enter text or URL"
              required
              disabled={isLoading}
            />
          )}

          {numberOption === "import" && (
            <div className="form-group">
              <label className="label-input">Upload a CSV file:</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                disabled={isLoading}
              />
            </div>
          )}
          {numberOption === "digits" && (
            <div className="form-group">
              <input
                type="number"
                value={numberOfDigits}
                onChange={(e) => setNumberOfDigits(e.target.value)}
                min="1"
                placeholder="Digits"
                required
                disabled={isLoading}
              />
              <input
                type="number"
                value={countToGenerate}
                onChange={(e) => setCountToGenerate(e.target.value)}
                min="1"
                placeholder="Count"
                required
                disabled={isLoading}
              />
              <input
                type="number"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                placeholder="Start"
                required
                disabled={isLoading}
              />
              <input
                type="number"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                placeholder="End"
                required
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="label-input">QR Code Size (inches):</label>
          <input
            type="number"
            value={qrCodeSizeInches}
            onChange={(e) => setQrCodeSizeInches(e.target.value)}
            min="0.1"
            max="5"
            step="0.1"
            required
            disabled={isLoading}
          />

          <label className="label-input">Upload a logo image:</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files[0]) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  setLogo(reader.result);
                };
                reader.readAsDataURL(e.target.files[0]);
              }
            }}
            disabled={isLoading}
          />
          <label className="label-input">Logo Scale (%):</label>
          <input
            type="number"
            value={logoScalePercent}
            onChange={(e) => {
              const v = Math.max(
                1,
                Math.min(1000, Number(e.target.value) || 1)
              );
              setLogoScalePercent(v);
            }}
            min="1"
            max="1000"
            step="1"
            required
            disabled={isLoading}
          />

          <label className="label-input">Logo Visibility (%):</label>
          <input
            type="number"
            value={transparency}
            onChange={(e) =>
              setTransparency(Math.min(Math.max(e.target.value, 0), 100))
            }
            min="0"
            max="100"
            step="1"
            required
            disabled={isLoading}
          />
        </div>

        <div className="button-container">
          <button type="submit" disabled={isLoading}>
            {isLoading ? "Generating…" : "Generate QR Codes"}
          </button>

          {qrCodes.length > 0 && (
            <>
              <button
                type="button"
                onClick={exportToPDF}
                className="export-button"
                disabled={isLoading}
              >
                Export to PDF
              </button>
              <button
                type="button"
                onClick={exportToCSV}
                className="export-button"
                disabled={isLoading}
              >
                Export to CSV
              </button>
              <button
                type="button"
                onClick={exportToCSV2}
                className="export-button"
                disabled={isLoading}
              >
                Export to CSV (Comma Separated)
              </button>
            </>
          )}
        </div>

        <div className="generated-numbers">
          <table className="number-table">
            <thead>
              <tr>
                <th>#</th>
                <th>QR Generated for</th>
              </tr>
            </thead>
            <tbody>
              {generatedNumbers.length > 0 && (
                <>
                  <tr onDoubleClick={handleRowDoubleClick}>
                    <td>1</td>
                    <td>{generatedNumbers[0]}</td>
                  </tr>
                  {showAll &&
                    generatedNumbers.slice(1).map((num, index) => (
                      <tr key={index + 1}>
                        <td>{index + 2}</td>
                        <td>{num}</td>
                      </tr>
                    ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </form>

      {error && <p className="error">{error}</p>}

      <div className="qr-codes" id="qrCodesContainer">
        {qrCodes && qrCodes.length > 0 ? (
          <table className="qr-code-table">
            <tbody>
              {Array.from({ length: Math.ceil(qrCodes.length / 5) }).map(
                (_, rowIndex) => (
                  <tr key={rowIndex}>
                    {qrCodes
                      .slice(rowIndex * 5, rowIndex * 5 + 5)
                      .map((url, index) => {
                        const globalIndex = rowIndex * 5 + index;
                        return (
                          <td key={index} className="qr-cell">
                            <div className="qr-container">
                              <span>{globalIndex + 1}</span>

                              <div
                                className="qr-image-wrapper"
                                style={{
                                  position: "relative",
                                  display: "inline-block",
                                }}
                              >
                                <img
                                  src={url}
                                  alt={`QR Code ${globalIndex + 1}`}
                                  className="qr-image"
                                  style={{
                                    width: `${qrCodeSizeInches * 80}px`,
                                    height: `${qrCodeSizeInches * 80}px`,
                                    display: "block",
                                  }}
                                />

                                {logo && (
                                  <img
                                    src={logo}
                                    alt="Logo"
                                    className="logo-image"
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      left: "50%",
                                      transform: "translate(-50%, -50%)",
                                      width: `${
                                        qrCodeSizeInches *
                                        80 *
                                        (logoScalePercent / 100)
                                      }px`,
                                      opacity: transparency / 100,
                                      pointerEvents: "none",
                                    }}
                                  />
                                )}
                              </div>
                            </div>

                            <div style={{ marginTop: 8 }}>
                              <button
                                type="button"
                                className="download-button"
                                onClick={() => downloadQRCode(url, globalIndex)}
                                disabled={isLoading}
                              >
                                {isLoading ? "Downloading…" : "Download PNG"}
                              </button>
                            </div>
                          </td>
                        );
                      })}
                  </tr>
                )
              )}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: "center", color: "#666" }}>
            No QR codes generated yet.
          </p>
        )}
      </div>
    </div>
  );
}

export default QRCodeGenerator;
